import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, MoreVertical } from 'lucide-react';
import { MembershipPlan } from '../../types/api';
import { membershipPlanAPI } from '../../services/api';
import { ToastContainer } from '../common/Toast';
import { useToast } from '../../hooks/useToast';
import { formatCurrency } from '../../utils/currency';
import { useAuth } from '../../contexts/AuthContext';

const MembershipPlans: React.FC = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<Partial<MembershipPlan>>({});
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  const [confirmDialogAction, setConfirmDialogAction] = useState<(() => void) | null>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  
  // Toast notifications
  const { toasts, removeToast, success, error } = useToast();
  
  // Confirmation dialog helper
  const showConfirmation = (message: string, onConfirm: () => void) => {
    setConfirmDialogMessage(message);
    setConfirmDialogAction(() => onConfirm);
    setShowConfirmDialog(true);
  };
  
  const handleConfirmAction = () => {
    if (confirmDialogAction) {
      confirmDialogAction();
    }
    setShowConfirmDialog(false);
    setConfirmDialogAction(null);
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };
    if (openDropdown !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const fetchPlans = async () => {
    try {
      const data = await membershipPlanAPI.getAll();
      setPlans(data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({});
    setShowModal(true);
  };

  const handleEdit = (plan: MembershipPlan) => {
    setFormData(plan);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (formData.membershipPlanId) {
        await membershipPlanAPI.update(formData.membershipPlanId, formData);
        success('Membership plan updated successfully!');
      } else {
        // Automatically add gymId from logged-in user when creating new plan
        const dataToCreate = {
          ...formData,
          gymId: user?.gymId
        };
        await membershipPlanAPI.create(dataToCreate);
        success('Membership plan created successfully!');
      }
      setShowModal(false);
      fetchPlans();
    } catch (err) {
      console.error('Error saving plan:', err);
      error('Failed to save membership plan');
    }
  };

  const handleDelete = async (id: number) => {
    showConfirmation(
      'Are you sure you want to delete this plan?',
      async () => {
        try {
          await membershipPlanAPI.delete(id);
          success('Membership plan deleted successfully!');
          fetchPlans();
        } catch (err) {
          console.error('Error deleting plan:', err);
          error('Failed to delete membership plan');
        }
      }
    );
  };

  if (loading) {
    return <div className="loading">Loading membership plans...</div>;
  }

  return (
    <div>
      <div className="header">
        <h1 className="header-title">Membership Plans</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          <Plus size={20} style={{ marginRight: '0.5rem' }} />
          Add Plan
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Plan Name</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.membershipPlanId}>
                    <td>{plan.planName}</td>
                    <td>{plan.planType}</td>
                    <td>{plan.durationInMonths} months</td>
                    <td>{formatCurrency(plan.price)}</td>
                    <td>
                      <span className={`badge ${plan.isActive ? 'badge-success' : 'badge-danger'}`}>
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ position: 'relative' }}>
                        <button 
                          className="btn btn-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(openDropdown === plan.membershipPlanId ? null : plan.membershipPlanId);
                          }}
                          style={{ padding: '0.25rem 0.5rem' }}
                          title="Actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                        
                        {openDropdown === plan.membershipPlanId && (
                          <div 
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: '100%',
                              marginTop: '0.25rem',
                              backgroundColor: 'white',
                              border: '1px solid #E5E7EB',
                              borderRadius: '6px',
                              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                              zIndex: 1000,
                              minWidth: '180px',
                              overflow: 'hidden'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(plan);
                                setOpenDropdown(null);
                              }}
                              style={{
                                width: '100%',
                                padding: '0.5rem 1rem',
                                textAlign: 'left',
                                border: 'none',
                                background: 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                cursor: 'pointer'
                              }}
                            >
                              <Edit size={16} />
                              Edit Plan
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(plan.membershipPlanId);
                                setOpenDropdown(null);
                              }}
                              style={{
                                width: '100%',
                                padding: '0.5rem 1rem',
                                textAlign: 'left',
                                border: 'none',
                                background: 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                cursor: 'pointer',
                                color: '#DC2626'
                              }}
                            >
                              <Trash2 size={16} />
                              Delete Plan
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {formData.membershipPlanId ? 'Edit Plan' : 'Add Plan'}
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="form">
              <div className="form-group">
                <label className="form-label">Plan Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.planName || ''}
                  onChange={(e) => setFormData({...formData, planName: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Plan Type</label>
                  <select
                    className="form-input"
                    value={formData.planType || ''}
                    onChange={(e) => setFormData({...formData, planType: e.target.value})}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Yearly">Yearly</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Duration (Months)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.durationInMonths || ''}
                    onChange={(e) => setFormData({...formData, durationInMonths: parseInt(e.target.value)})}
                    required
                    min="1"
                    max="24"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Price (₹)</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.price || ''}
                  onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                  required
                  step="1"
                  min="0"
                  placeholder="e.g., 3999"
                />
                <small style={{ color: '#6B7280', fontSize: '0.875rem' }}>Enter amount in Indian Rupees</small>
              </div>
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>
              
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {formData.membershipPlanId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Confirm Action</h2>
              <button className="modal-close" onClick={() => setShowConfirmDialog(false)}>×</button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              <p style={{ margin: 0, fontSize: '1rem', lineHeight: '1.5' }}>{confirmDialogMessage}</p>
            </div>
            
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmAction}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default MembershipPlans;
